// src/components/RunBookModal.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { isPermissionDenied, getPermissionDeniedMessage } from "@1823-partners/core";
import { getAccounts } from "../api";

const RunBookModal = ({
  open,
  onClose,
  onConfirm,
  selectedAccounts: initialSelectedAccounts = []
}) => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set(initialSelectedAccounts));
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState(null);
  const [expandedPrefixes, setExpandedPrefixes] = useState(new Set());
  const hasOpenedRef = useRef(false);

  // Extract prefix from account name (up to first space, -, ., or _)
  const getAccountPrefix = useCallback((accountName) => {
    if (!accountName) return '';
    const match = String(accountName).match(/^([^\s._-]+)/);
    return match ? match[1] : accountName;
  }, []);

  // Group accounts by prefix
  const getAccountsByPrefix = useMemo(() => {
    const grouped = {};
    accounts.forEach(account => {
      const accountName = account.ACCOUNT_NAME || account.ACCOUNT_ID || account.account_name || account.account_id || account.name || account.id || account;
      const prefix = getAccountPrefix(accountName);
      if (!grouped[prefix]) {
        grouped[prefix] = [];
      }
      grouped[prefix].push(account);
    });
    // Sort prefixes alphabetically
    return Object.keys(grouped).sort().reduce((acc, prefix) => {
      acc[prefix] = grouped[prefix];
      return acc;
    }, {});
  }, [accounts, getAccountPrefix]);

  const fetchAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getAccounts();
      const data = response?.data;

      // Support both: ["acct1", "acct2"] and [{...}, {...}]
      const normalized = Array.isArray(data)
        ? data.map((a) => {
          if (typeof a === 'string') {
            return { ACCOUNT_ID: a, ACCOUNT_NAME: a };
          }
          if (a && typeof a === 'object') {
            return a;
          }
          return { ACCOUNT_ID: String(a), ACCOUNT_NAME: String(a) };
        })
        : [];

      setAccounts(normalized);
    } catch (e) {
      console.error('Error fetching accounts:', e);
      setAccounts([]);
      if (isPermissionDenied(e)) {
        setError(getPermissionDeniedMessage(e));
      } else {
        setError('Failed to load accounts. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      hasOpenedRef.current = false;
      return;
    }

    if (!hasOpenedRef.current) {
      // Only reset selected accounts when modal first opens, not on every render
      setSelectedAccounts(new Set(initialSelectedAccounts));
      setSearchText("");
      fetchAccounts();
      hasOpenedRef.current = true;
    }
  }, [open, fetchAccounts, initialSelectedAccounts]); // reset only when opened

  // Expand all prefix groups when accounts are loaded
  useEffect(() => {
    if (accounts.length > 0) {
      const prefixes = Object.keys(getAccountsByPrefix);
      setExpandedPrefixes(new Set(prefixes));
    }
  }, [accounts, getAccountsByPrefix]);

  const handleToggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const filteredAccounts = getFilteredAccounts();
    const allSelected = filteredAccounts.every(account => {
      const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
      return selectedAccounts.has(accountId);
    });

    setSelectedAccounts(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all filtered accounts
        filteredAccounts.forEach(account => {
          const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
          newSet.delete(accountId);
        });
      } else {
        // Select all filtered accounts
        filteredAccounts.forEach(account => {
          const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
          newSet.add(accountId);
        });
      }
      return newSet;
    });
  };

  const handleTogglePrefix = (prefix) => (event, isExpanded) => {
    setExpandedPrefixes(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(prefix);
      } else {
        newSet.delete(prefix);
      }
      return newSet;
    });
  };

  const handleSelectAllInPrefix = (prefix) => {
    // Only the accounts the search shows: the checkbox state is computed from
    // them, and a hidden account must never be queued for a book run.
    const prefixAccounts = getFilteredAccountsByPrefix[prefix] || [];
    const allSelected = prefixAccounts.every(account => {
      const accountId = getAccountId(account);
      return selectedAccounts.has(accountId);
    });

    setSelectedAccounts(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all accounts in this prefix
        prefixAccounts.forEach(account => {
          const accountId = getAccountId(account);
          newSet.delete(accountId);
        });
      } else {
        // Select all accounts in this prefix
        prefixAccounts.forEach(account => {
          const accountId = getAccountId(account);
          newSet.add(accountId);
        });
      }
      return newSet;
    });
  };

  const getFilteredAccounts = useCallback(() => {
    if (!searchText.trim()) {
      return accounts;
    }
    const searchLower = searchText.toLowerCase();
    return accounts.filter(account => {
      const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
      const accountName = account.ACCOUNT_NAME || account.account_name || account.name || '';
      const accountCode = account.ACCOUNT_CODE || account.account_code || account.code || '';

      return (
        String(accountId).toLowerCase().includes(searchLower) ||
        String(accountName).toLowerCase().includes(searchLower) ||
        String(accountCode).toLowerCase().includes(searchLower)
      );
    });
  }, [accounts, searchText]);

  // Get filtered accounts grouped by prefix
  const getFilteredAccountsByPrefix = useMemo(() => {
    const filtered = getFilteredAccounts();
    const grouped = {};
    filtered.forEach(account => {
      const accountName = account.ACCOUNT_NAME || account.ACCOUNT_ID || account.account_name || account.account_id || account.name || account.id || account;
      const prefix = getAccountPrefix(accountName);
      if (!grouped[prefix]) {
        grouped[prefix] = [];
      }
      grouped[prefix].push(account);
    });
    // Sort prefixes alphabetically
    return Object.keys(grouped).sort().reduce((acc, prefix) => {
      acc[prefix] = grouped[prefix];
      return acc;
    }, {});
  }, [getAccountPrefix, getFilteredAccounts]);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(Array.from(selectedAccounts));
    }
    onClose();
  };

  const filteredAccounts = getFilteredAccounts();
  const allFilteredSelected = filteredAccounts.length > 0 &&
    filteredAccounts.every(account => {
      const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
      return selectedAccounts.has(accountId);
    });
  const someFilteredSelected = filteredAccounts.some(account => {
    const accountId = account.ACCOUNT_ID || account.account_id || account.id || account;
    return selectedAccounts.has(accountId);
  });

  // Determine account identifier and display name
  const getAccountDisplay = (account) => {
    if (typeof account === 'string') {
      return account;
    }
    const accountId = account.ACCOUNT_ID || account.account_id || account.id || '';
    const accountName = account.ACCOUNT_NAME || account.account_name || account.name || '';
    // If accountId and accountName are the same, just return one to avoid duplication
    if (accountId === accountName) {
      return accountId;
    }
    return accountName ? `${accountId} - ${accountName}` : accountId;
  };

  const getAccountId = (account) => {
    if (typeof account === 'string') {
      return account;
    }
    return account.ACCOUNT_ID || account.account_id || account.id || account;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        pb: 1
      }}>
        <Typography variant="h6">
          Select Accounts for Run Book
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error">{error}</Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button onClick={fetchAccounts}>Retry</Button>
            </Box>
          </Box>
        ) : (
          <>
            <TextField
              fullWidth
              placeholder="Search accounts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected && !allFilteredSelected}
                    onChange={handleSelectAll}
                  />
                }
                label={
                  <Typography variant="subtitle2" fontWeight="bold">
                    Select All ({filteredAccounts.length} accounts)
                  </Typography>
                }
              />
              <Typography variant="body2" color="text.secondary">
                {selectedAccounts.size} selected
              </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {Object.keys(getFilteredAccountsByPrefix).length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="text.secondary">No accounts found</Typography>
                </Box>
              ) : (
                Object.entries(getFilteredAccountsByPrefix).map(([prefix, prefixAccounts]) => {
                  const isExpanded = expandedPrefixes.has(prefix);
                  const allPrefixSelected = prefixAccounts.every(account => {
                    const accountId = getAccountId(account);
                    return selectedAccounts.has(accountId);
                  });
                  const somePrefixSelected = prefixAccounts.some(account => {
                    const accountId = getAccountId(account);
                    return selectedAccounts.has(accountId);
                  });

                  return (
                    <Accordion
                      key={prefix}
                      expanded={isExpanded}
                      onChange={handleTogglePrefix(prefix)}
                      sx={{
                        mb: 1,
                        '&:before': { display: 'none' },
                        boxShadow: 1
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: 'grey.50',
                          '&:hover': { backgroundColor: 'grey.100' }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mr: 1 }}>
                          <Checkbox
                            checked={allPrefixSelected}
                            indeterminate={somePrefixSelected && !allPrefixSelected}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAllInPrefix(prefix);
                            }}
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                            {prefix} ({prefixAccounts.length})
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                            {prefixAccounts.filter(acc => selectedAccounts.has(getAccountId(acc))).length} selected
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense>
                          {prefixAccounts.map((account, index) => {
                            const accountId = getAccountId(account);
                            const isSelected = selectedAccounts.has(accountId);
                            const displayText = getAccountDisplay(account);

                            return (
                              <ListItem
                                key={accountId || index}
                                disablePadding
                                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                              >
                                <ListItemButton
                                  onClick={() => handleToggleAccount(accountId)}
                                  dense
                                  sx={{ pl: 4 }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText
                                    primary={displayText}
                                    primaryTypographyProps={{
                                      sx: { fontSize: '0.875rem' }
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedAccounts.size === 0}
        >
          Run Book ({selectedAccounts.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RunBookModal;
